import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ReviewsSection from "@/components/ReviewsSection";
import PriceCalculator from "@/components/PriceCalculator";
import ExchangeRates from "@/components/ExchangeRates";
import ReferralBanner from "@/components/ReferralBanner";
import HowItWorks from "@/components/HowItWorks";
import VoiceAssistant from "@/components/VoiceAssistant";
import { Shield, Zap, CheckCircle2, Globe2, MessageCircle } from "lucide-react";
import { SUPPORT_NUMBERS } from "@/lib/constants";
import { useSiteContent } from "@/lib/siteContent";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG_DEFAULT = "https://customer-assets-cm19k8pv.emergentagent.net/job_40b1db31-0060-4063-a1ad-4ad354d6a278/artifacts/6374ddc24df70335_DCBED54C-DDBD-401F-A171-3A17253C1332.png";
const APP_IMG = "https://images.unsplash.com/photo-1616077168712-fc6c788db4af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxtb25leSUyMHRyYW5zZmVyJTIwbW9iaWxlJTIwYXBwJTIwYWJzdHJhY3R8ZW58MHx8fHwxNzgwOTI5OTE4fDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const sc = useSiteContent();
  const { user } = useAuth();
  const navigate = useNavigate();
  const HERO_IMG = sc.hero_image_url || HERO_IMG_DEFAULT;

  // Admin using the installed PWA (standalone display mode) → jump straight to the admin dashboard
  // instead of showing the marketing landing every time they open the app.
  useEffect(() => {
    if (!user) return;
    if (user.role !== "admin") return;
    const isStandalone =
      (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      (typeof navigator !== "undefined" && navigator.standalone === true);
    if (isStandalone) {
      navigate("/admin", { replace: true });
    }
  }, [user, navigate]);
  // Track unique site visit (fire-and-forget; no PII)
  useEffect(() => {
    try {
      let sid = localStorage.getItem("sl_visitor_id");
      if (!sid) {
        sid = (crypto?.randomUUID?.() || (Date.now().toString(36) + Math.random().toString(36).slice(2, 10)));
        localStorage.setItem("sl_visitor_id", sid);
      }
      api.post("/track-visit", { session_id: sid, path: "/" }).catch(() => {});
    } catch (_e) { /* ignore */ }
  }, []);

  // Inject Organization + FinancialService JSON-LD for SEO knowledge panel
  useEffect(() => {
    const origin = window.location.origin;
    const schema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": `${origin}/#org`,
          "name": "Swiftlink-Africa",
          "url": origin,
          "logo": `${origin}/logo192.png`,
          "description": "Transfert d'argent rapide et sécurisé entre le Togo, le Gabon, la France et le Rwanda via Mobile Money, PayPal, Wero et virement instantané SEPA.",
          "areaServed": [
            { "@type": "Country", "name": "Togo" },
            { "@type": "Country", "name": "Gabon" },
            { "@type": "Country", "name": "France" },
            { "@type": "Country", "name": "Rwanda" },
          ],
          "contactPoint": [
            {
              "@type": "ContactPoint",
              "telephone": "+33-7-53-72-81-95",
              "contactType": "customer support",
              "areaServed": "FR",
              "availableLanguage": ["French"],
              "contactOption": "WhatsApp",
            },
            {
              "@type": "ContactPoint",
              "telephone": "+250-787-195-886",
              "contactType": "customer support",
              "areaServed": "RW",
              "availableLanguage": ["French", "English"],
              "contactOption": "WhatsApp",
            },
            {
              "@type": "ContactPoint",
              "telephone": "+228-99-82-57-04",
              "contactType": "customer support",
              "areaServed": "TG",
              "availableLanguage": ["French"],
              "contactOption": "WhatsApp",
            },
          ],
        },
        {
          "@type": "FinancialService",
          "@id": `${origin}/#service`,
          "name": "Swiftlink-Africa — Transfert d'argent",
          "url": origin,
          "description": "Service de transfert d'argent international Togo · Gabon · France · Rwanda. Dépôt Mobile Money (Moov, TMoney, Airtel), réception MTN/Tigo/Airtel Rwanda ou PayPal/Wero/Virement instantané France. Vérification manuelle sous 1 heure.",
          "provider": { "@id": `${origin}/#org` },
          "areaServed": ["TG", "GA", "FR"],
          "currenciesAccepted": ["XOF", "XAF", "EUR"],
          "paymentAccepted": ["Mobile Money", "PayPal", "Wero", "SEPA Instant"],
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            "opens": "00:00",
            "closes": "23:59",
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Corridors disponibles",
            "itemListElement": [
              {
                "@type": "Offer",
                "name": "Togo → Gabon",
                "description": "Mobile Money Togo (Moov, TMoney) vers Mobile Money Gabon (Airtel, Moov). 5% de frais.",
                "priceSpecification": { "@type": "PriceSpecification", "price": "5", "priceCurrency": "PERCENT" },
              },
              {
                "@type": "Offer",
                "name": "Gabon → France",
                "description": "Airtel Money Gabon vers PayPal / Wero / Virement instantané France. 15% de frais.",
                "priceSpecification": { "@type": "PriceSpecification", "price": "15", "priceCurrency": "PERCENT" },
              },
              {
                "@type": "Offer",
                "name": "Gabon → Rwanda",
                "description": "Airtel Money Gabon vers MTN / Tigo / Airtel Money Rwanda. 12,5% de frais.",
                "priceSpecification": { "@type": "PriceSpecification", "price": "12.5", "priceCurrency": "PERCENT" },
              },
            ],
          },
        },
      ],
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "sl-org-jsonld";
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => script.remove();
  }, []);

  return (
    <div className="min-h-screen sl-hero-bg">
      <Header />
      <ReferralBanner />
      <VoiceAssistant />

      {/* HERO */}
      <section className="pt-12 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 sl-fadeup" data-testid="hero-section">
            <div className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-[var(--sl-primary)] font-semibold shadow-sm">
              <span className="w-2 h-2 rounded-full bg-[var(--sl-secondary)] animate-pulse" />
              🇹🇬 Togo · 🇬🇦 Gabon · 🇫🇷 France · 🇷🇼 Rwanda
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-[64px] font-bold mt-6 tracking-tight leading-[1.04]">
              Envoyez de l'argent<br /><span className="text-[var(--sl-primary)]">en toute confiance.</span>
            </h1>
            <p className="mt-6 text-lg text-gray-600 max-w-xl leading-relaxed">
              {sc.hero_subtitle || (
                <>Swiftlink-Africa relie le Togo, le Gabon, la France et le Rwanda avec un transfert mobile money simple, transparent et <span className="text-gray-900 font-medium">vérifié manuellement</span> par notre équipe.</>
              )}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/envoyer" className="sl-btn-primary" data-testid="hero-cta-send">
                Envoyer maintenant
              </Link>
              <Link to="/suivi" className="sl-btn-outline" data-testid="hero-cta-track">
                Suivre un envoi
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-[11px] sm:text-sm text-gray-500">
              <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                <Shield size={14} className="flex-shrink-0 sm:w-4 sm:h-4 text-[var(--sl-primary)]" />
                <span className="truncate">Vérification manuelle</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                <Zap size={14} className="flex-shrink-0 sm:w-4 sm:h-4 text-[var(--sl-primary)]" />
                <span className="truncate">Traitement rapide</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                <Globe2 size={14} className="flex-shrink-0 sm:w-4 sm:h-4 text-[var(--sl-primary)]" />
                <span className="truncate">XOF → XAF → EUR</span>
              </div>
            </div>
          </div>

          <div className="md:col-span-5 relative sl-fadeup" style={{ animationDelay: "0.15s" }}>
            <div className="relative rounded-3xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(10,77,60,0.35)] border border-white">
              <img src={HERO_IMG} alt="Femme africaine utilisant Swiftlink depuis son téléphone" className="w-full h-[480px] object-cover" />
              <div className="absolute inset-x-4 bottom-4 bg-white/95 backdrop-blur-md rounded-2xl p-5 border border-white">
                <div className="text-xs uppercase tracking-widest text-[var(--sl-secondary-dark)] font-semibold">Dernier transfert</div>
                <div className="mt-2 flex items-end justify-between">
                  <div>
                    <div className="text-2xl font-heading font-bold">50 000 XOF</div>
                    <div className="text-xs text-gray-500">→ Libreville, Gabon</div>
                  </div>
                  <span className="sl-badge sl-badge-completed">Terminé</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust band — institutional polish */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
          <div className="rounded-2xl bg-white border border-gray-200/80 px-6 py-5 sm:py-6 shadow-sm grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 text-center">
            <TrustStat label="Lancée en" value="2026" />
            <TrustStat label="Réponse < " value="1 heure" />
            <TrustStat label="Corridors actifs" value="4 pays" />
            <TrustStat label="Vérification" value="100% humaine" />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — corridor-aware */}
      <HowItWorks />

      {/* PRICE CALCULATOR */}
      <PriceCalculator />

      {/* EXCHANGE RATES */}
      <ExchangeRates />

      {/* FEATURE / TRUST */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <img src={APP_IMG} alt="Mobile money" className="rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.08)] w-full h-[420px] object-cover" />
          </div>
          <div className="md:col-span-6 order-1 md:order-2">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--sl-secondary-dark)]">Sécurité & Transparence</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-3">{sc.trust_title || "Chaque transfert est tracé."}</h2>
            <p className="mt-4 text-gray-600 leading-relaxed">
              {sc.trust_body || "Un identifiant unique pour chaque transaction, une chronologie complète accessible à tout moment, et une validation humaine pour éviter toute erreur."}
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Identifiant unique de transaction (ex: SL-XXXXXXXX)",
                "Suivi en temps réel du statut",
                "Validation manuelle par administrateur",
                "Historique complet des actions",
              ].map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <CheckCircle2 className="text-[var(--sl-primary)] mt-0.5 flex-shrink-0" size={20} />
                  <span className="text-gray-700">{f}</span>
                </li>
              ))}
            </ul>
            <Link to="/envoyer" className="sl-btn-primary mt-8" data-testid="trust-cta-send">
              Commencer un envoi
            </Link>
          </div>
        </div>
      </section>

      {/* SUPPORT */}
      <section className="py-20 bg-white border-t border-gray-200" id="support">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--sl-secondary-dark)]">Service client</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-3">Une question ? Parlons.</h2>
            <p className="mt-3 text-gray-600">
              Notre équipe répond 7j/7 sur WhatsApp. Cliquez sur votre pays pour démarrer la conversation.
            </p>
          </div>
          <div className="mt-12 grid sm:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {SUPPORT_NUMBERS.map((s, i) => (
              <a
                key={s.country}
                href={s.waLink}
                target="_blank"
                rel="noreferrer"
                className="sl-card sl-card-hoverable hover:border-[var(--sl-primary)] group"
                data-testid={`landing-support-${s.country.toLowerCase()}`}
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="flex items-start justify-between">
                  <span className="text-4xl leading-none">{s.flag}</span>
                  <MessageCircle size={20} className="text-[#25D366]" />
                </div>
                <div className="mt-5">
                  <div className="text-xs uppercase tracking-widest text-gray-500">Support {s.country}</div>
                  <div className="font-heading text-xl font-bold mt-1 group-hover:text-[var(--sl-primary)] transition-colors">
                    {s.phone}
                  </div>
                </div>
                <div className="mt-4 inline-flex items-center gap-2 text-xs text-gray-500">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  Disponible sur WhatsApp
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="relative overflow-hidden rounded-3xl p-12 text-center text-white"
            style={{ background: "linear-gradient(135deg, var(--sl-primary-dark), var(--sl-primary))" }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold">Prêt à envoyer ?</h2>
            <p className="mt-3 text-white/80 max-w-xl mx-auto">
              Quelques minutes suffisent. Aucune création de compte obligatoire.
            </p>
            <div className="mt-8 flex justify-center gap-3 flex-wrap">
              <Link to="/envoyer" className="sl-btn-secondary" data-testid="cta-send">
                Démarrer maintenant
              </Link>
              <Link to="/suivi" className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 font-medium border-2 border-white/40 text-white hover:bg-white/10" data-testid="cta-track">
                Suivre une transaction
              </Link>
            </div>
          </div>
        </div>
      </section>

      <ReviewsSection />

      <Footer />
    </div>
  );
}

function TrustStat({ label, value }) {
  return (
    <div className="px-2">
      <div className="font-heading text-2xl sm:text-3xl font-bold text-[var(--sl-primary)] tracking-tight">{value}</div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 font-medium mt-1">{label}</div>
    </div>
  );
}
